import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ShoppingBag, Palette, LayoutGrid, Settings } from 'lucide-react';

const ShopifyDevelopment = () => {
  const quoteRef = useRef(null);
  const contentRef = useRef(null);
  const processRef = useRef(null);
  
  const isQuoteInView = useInView(quoteRef, { once: true, margin: "-100px" });
  const isContentInView = useInView(contentRef, { once: true, margin: "-100px" });
  const isProcessInView = useInView(processRef, { once: true, margin: "-100px" });

  const services = [
    { title: "Custom Store Setup", icon: ShoppingBag },
    { title: "Theme Development & Design", icon: Palette },
    { title: "Shopify Apps Integration", icon: LayoutGrid },
    { title: "Ongoing Maintenance", icon: Settings },
  ];

  const processSteps = [
    {
      title: "Store Design & Setup",
      description: "We create an intuitive and visually appealing Shopify store that's easy to navigate and aligned with your brand's aesthetic."
    },
    {
      title: "Product Listings & Descriptions",
      description: "Ensuring your products are listed with detailed, keyword-optimized descriptions, high-quality images, and clear pricing."
    },
    {
      title: "Checkout Optimization",
      description: "Streamlining the checkout process to reduce cart abandonment and increase conversions."
    },
    {
      title: "App Integrations",
      description: "Adding essential Shopify apps to enhance functionality, such as inventory management, reviews, and customer support tools."
    },
    {
      title: "Ongoing Maintenance & Updates",
      description: "Providing continued support, adding new features, and ensuring your store remains fast, secure, and user-friendly."
    }
  ];

  return (
    <div className="relative min-h-screen bg-[#b3ebf2] overflow-hidden px-4 sm:px-6">
      {/* Animated background elements */}
      <motion.div className="absolute inset-0">
        <motion.div 
          className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#ffd54f] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-[#81deea] rounded-full mix-blend-multiply filter blur-xl opacity-30"
          animate={{ scale: [1.2, 1, 1.2], rotate: [90, 0, 90] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear", delay: 10 }}
        />
      </motion.div>

      <motion.div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_2px,transparent_2px),linear-gradient(90deg,rgba(0,0,0,0.02)_2px,transparent_2px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"/>

      <div className="relative max-w-7xl mx-auto pt-20 sm:pt-32 pb-16">
        {/* Breadcrumb */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto mb-8 text-gray-600"
        >
          
        </motion.div>

        {/* Quote Section */}
        <motion.div
          ref={quoteRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isQuoteInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-[#f4b400] bg-clip-text text-transparent">
            Shopify Development
          </h1>
          <p className="text-xl md:text-2xl italic text-gray-600">
            "It's very easy to be different but very difficult to be better."
            <span className="block mt-2 text-base">— Jonathan Ive</span>
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-4 text-center"
            >
              <service.icon className="w-8 h-8 mx-auto mb-2 text-[#ffd54f]" />
              <h3 className="text-sm md:text-base font-medium text-gray-800">{service.title}</h3>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isContentInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16 text-gray-700"
        >
          <p className="text-lg leading-relaxed mb-8">
            No matter where you are currently, our team will consult with you one on one to learn your market, customers and competitive trends, and set up an online store for you that's not only functional but <span className="font-bold text-[#f4b400]">built for growth</span>. From design to conversion optimization, we make sure your Shopify store is built to thrive.
          </p>
        </motion.div>

        {/* Process Section */}
        <motion.div
          ref={processRef}
          initial={{ opacity: 0, y: 20 }}
          animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-gray-800">Process</h2>
          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                animate={isProcessInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-6 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-xl font-semibold mb-2 text-[#ffd54f]">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="p-8 bg-white/80 backdrop-blur-sm rounded-2xl">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">GROVIYO WILL:</h2>
            <p className="text-lg text-gray-700 mb-6">
              Build a shop that blossoms. From initial setup with stunning design to traffic strategy and implementation, our Shopify services ensure your online store flourishes.
            </p>
            <p className="italic text-black font-medium">
              "thank you. it's so easy to grow with groviyo!" –shameless self promotion
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ShopifyDevelopment;